import React from 'react';

import {
	FormattedDate,
	FormattedMessage,
	FormattedRelativeTime,
	type MessageDescriptor,
} from 'react-intl';

import { selectUnit } from '@atlassian/recent-work-ui';

import { isMoreThanOneWeekAgo } from '../../../../../common/ui/event-type-message';
import { type ActivityEnrichment } from '../../../../../common/ui/quick-find/types';

import {
	type EventTypeMessages,
	icuCommentedEventTypeMessages,
	icuCreatedEventTypeMessages,
	icuEditedEventTypeMessages,
	icuInteractedEventTypeMessages,
	icuMentionedEventTypeMessages,
	icuReactedEventTypeMessages,
	icuReadEventTypeMessages,
	icuSharedEventTypeMessages,
	icuViewedEventTypeMessages,
} from './messages';
import { parseRecommendedReason, RecommendedEventType, RecommendedType } from './types';
import { resolveDisplayTimestamp } from './utils';

const ICU_EVENT_TYPE_MESSAGES: Record<RecommendedEventType, EventTypeMessages> = {
	// `clicked` corresponds to the backend `sl-clicked` (search-link clicked) signal.
	// For consistency with the recent behaviour applied elsewhere in quick-find — where
	// `sl-clicked` events are surfaced to users as "viewed" — we intentionally map the
	// `clicked` event type to the ICU `viewed` messages here. This avoids exposing an
	// internal click-tracking signal as a user-facing "clicked" action and keeps the
	// wording aligned with the rest of the recommended/recent activity UX.
	clicked: icuViewedEventTypeMessages,
	commented: icuCommentedEventTypeMessages,
	created: icuCreatedEventTypeMessages,
	edited: icuEditedEventTypeMessages,
	interacted: icuInteractedEventTypeMessages,
	mentioned: icuMentionedEventTypeMessages,
	reacted: icuReactedEventTypeMessages,
	read: icuReadEventTypeMessages,
	shared: icuSharedEventTypeMessages,
	viewed: icuViewedEventTypeMessages,
};

/**
 * Returns the appropriate message descriptor for a given event type.
 * @param eventType - The type of the event.
 * @param options - Options to configure the message.
 * @returns The message descriptor for the event type.
 */
const messageForEventType = (
	eventType: RecommendedEventType,
	options: { isAbsolute?: boolean } = {},
): MessageDescriptor => {
	const { isAbsolute = true } = options;
	const eventTypeMessage = ICU_EVENT_TYPE_MESSAGES.hasOwnProperty(eventType)
		? ICU_EVENT_TYPE_MESSAGES[eventType]
		: icuViewedEventTypeMessages;

	return isAbsolute ? eventTypeMessage.absolute : eventTypeMessage.relative;
};

// Sort enum values by length descending to avoid substring false positives,
const sortedRecommendedEventTypes = Object.values(RecommendedEventType).sort(
	(a, b) => b.length - a.length,
);

/**
 * Returns the `feature` value from `reason` if it matches one of the keys in
 * `ICU_EVENT_TYPE_MESSAGES` (case-insensitive), otherwise returns `null`.
 */
export const getEventTypeFromReasonFeature = (
	feature: string | undefined,
): RecommendedEventType | null => {
	if (!feature) {
		return null;
	}
	const lowerFeature = feature.toLowerCase();
	if (ICU_EVENT_TYPE_MESSAGES.hasOwnProperty(lowerFeature)) {
		return lowerFeature as RecommendedEventType;
	}
	return null;
};

/**
 * Extracts the event type from `reason.text` by checking if the text contains
 * a known event type keyword. Returns the matching `RecommendedEventType` value,
 * or `RecommendedEventType.VIEWED` as a default fallback.
 */
export const extractEventTypeFromReasonText = (text: string): RecommendedEventType => {
	const lowerText = text.toLowerCase();

	for (const eventType of sortedRecommendedEventTypes) {
		if (lowerText.includes(eventType)) {
			return eventType;
		}
	}

	return RecommendedEventType.VIEWED;
};

type RecommendedDocumentEventTypeMessageProps = {
	reason: string;
	activityEnrichment?: ActivityEnrichment;
	logException: (error: Error) => Promise<void>;
};

export const RecommendedDocumentEventTypeMessage = ({
	reason,
	activityEnrichment,
	logException,
}: RecommendedDocumentEventTypeMessageProps): React.ReactNode => {
	const parsedReason = parseRecommendedReason(reason, logException);

	if (!parsedReason) {
		return null;
	}

	const eventType =
		getEventTypeFromReasonFeature(parsedReason.feature) ??
		extractEventTypeFromReasonText(parsedReason.text);

	const { type } = parsedReason;

	const timestamp = resolveDisplayTimestamp(
		type,
		eventType,
		parsedReason.timestamp,
		activityEnrichment,
	);

	const isAbsolute = isMoreThanOneWeekAgo(timestamp);

	const message = messageForEventType(eventType, { isAbsolute });

	const { value, unit } = selectUnit(new Date(timestamp).getTime(), new Date(), {
		day: 7,
	});

	const values = isAbsolute
		? {
				// Show the absolute time if the activity was more than a week ago
				// e.g: You viewed on January 1, 2021
				absoluteTime: (
					<FormattedDate value={timestamp} month="short" day="2-digit" year="numeric" />
				),
			}
		: {
				// Show the relative time if the activity was within a week ago
				// e.g: You viewed 2 hours ago
				relativeTime: <FormattedRelativeTime value={value} unit={unit} />,
			};

	// Use ICU select so translators get full sentence context and verb conjugation is correct.
	// `actor` is 'you' or 'collaborator'; `username` is the `other` branch for real names in future.
	const actor = type === RecommendedType.NO_COLLABORATOR ? 'you' : 'collaborator';
	return <FormattedMessage {...message} values={{ ...values, actor, username: '' }} />;
};
